import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { FormValidationContext } from '../../benefit/common/FormValidationContext';
import { useUI } from '../../../context/UIContext';
import { calculatorService } from '../../../services/calculatorService';
import { useBootstrapValidation } from '../hooks/userBootstrapValidation';
import { useHydratedForm } from '../hooks/useHydratedForm';
import { hasMeaningfulChange } from '../../../utils/hasMeaningfulChange';
import PercentageField from '../../benefit/fields/PercentageField';
import EffectiveDateField from '../common/EffectiveDateField';
import { formatTier } from '../../../utils/formatters';
import { useState } from 'react';
import { MAX_AMI, MAX_FPL_OR, MAX_FPL_WA, MAX_SMI, STATES } from '../../../constants/constants';

const mapTierToForm = (tier) => ({
  id: tier.id,
  tier: tier.tier,
  state: tier.state,
  energyDiscount: tier.energyDiscount ?? '',
  arrearage: tier.arrearage ?? '',
  amiMax: tier.amiMax ?? '',
  fplMax: tier.fplMax ?? '',
  smiMax: tier.smiMax ?? '',
  effectiveDate: tier.effectiveDate ?? ''
});

const EditTierModal = ({ tier, onSave, onClose }) => {
  const { validated, validateForm } = useBootstrapValidation();
  const [form, setForm] = useHydratedForm(tier, mapTierToForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customError, setCustomError] = useState('');
  const { showLoading, hideLoading, showError, showSuccess } = useUI();

  if (!tier) return null;
  const MEANINGFUL_FIELDS = [
    'energyDiscount',
    'arrearage',
    'amiMax',
    'fplMax',
    'smiMax',
    'effectiveDate',
  ];

  const formattedTier = formatTier(tier.state, tier.tier);
  const modalTitle = new Date(tier.effectiveDate) > new Date()
    ? `Edit Upcoming Tier ${formattedTier}`
    : `Edit Tier ${formattedTier}`;

  const handleSubmit = async (e) => {
    const formEl = e.currentTarget;
    e.preventDefault();

    if (!hasMeaningfulChange(tier, form, MEANINGFUL_FIELDS)) {
      setCustomError(
        'At least one value must be changed before saving.'
      );
      return; 
    }

    if (!validateForm(formEl)) return;

    setIsSubmitting(true)
;    showLoading('Saving tier configuration…');

    try {
      const response = await calculatorService.saveTier(form);
      setIsSubmitting(false);

      if (response.success) {
        onSave(response.data);
        onClose();
        showSuccess(`${formattedTier} configuration saved.`);
      } else {
        showError(
          response.error || 'Failed to save tier configuration.'
        );
      }
    } finally {
      setIsSubmitting(false);
      hideLoading();
    }
  };

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <FormValidationContext.Provider value={validated}>
          <Modal.Header closeButton>
            <Modal.Title>{modalTitle}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row>
              <Col>
                <PercentageField
                  value={form.energyDiscount}
                  required
                  fieldName="Energy Discount"
                  onChange={(v) => setForm({ ...form, energyDiscount: v })}
                  isSubmitting={isSubmitting}
                />
              </Col>
              <Col>
                <PercentageField
                  value={form.arrearage}
                  required
                  fieldName="Arrearage"
                  onChange={(v) => setForm({ ...form, arrearage: v })}
                  isSubmitting={isSubmitting}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              {tier.state === STATES.WA && (
                <Col>
                    <PercentageField
                      value={form.amiMax}
                      required
                      fieldName="AMI max"
                      max={MAX_AMI}
                      onChange={(v) => setForm({ ...form, amiMax: v })}
                      isSubmitting={isSubmitting}
                    />
                </Col>
              )}
              <Col md={6}>
                <PercentageField
                  value={form.fplMax}
                  required
                  fieldName="FPL Max"
                  max={tier.state === STATES.WA? MAX_FPL_WA: MAX_FPL_OR}
                  onChange={(v) => setForm({ ...form, fplMax: v })}
                  isSubmitting={isSubmitting}
                />
              </Col>
              {tier.state === STATES.OR && (
                <Col>
                    <PercentageField
                      value={form.smiMax}
                      required
                      max={MAX_SMI}
                      fieldName="SMI max"
                      onChange={(v) => setForm({ ...form, smiMax: v })}
                      isSubmitting={isSubmitting}
                    />
                </Col>
              )}
            </Row>
            <Row className="mt-3">
              <Col md={6}>
                <EffectiveDateField
                  value={form.effectiveDate}
                  onChange={(v) => setForm({ ...form, effectiveDate: v })}
                  isSubmitting={isSubmitting}
                />
              </Col>
            </Row>
            {customError && (
              <div className="text-danger mt-3">{customError}</div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </FormValidationContext.Provider>
      </Form>
    </Modal>
  );
};

export default EditTierModal;